import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AppShell from '../components/AppShell';
import { useUnits } from '../lib/units';
import { apiFetch, ApiError } from '../lib/api';
import { fmtFileSize } from '../lib/format';
import type { UploadResponse } from '../lib/types';

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp']);

export default function Upload() {
    const { t } = useTranslation();
    const { active, activeCntId } = useUnits();
    const navigate = useNavigate();

    const [file,  setFile]  = useState<File | null>(null);
    const [notes, setNotes] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    function pickFile(f: File | null) {
        setErr(null);
        if (!f) { setFile(null); return; }
        if (f.size > MAX_BYTES) { setErr(t('upload.tooLarge')); return; }
        if (f.type && !ALLOWED.has(f.type)) {
            // Server is the authoritative gate; surface a hint up front but
            // let the request proceed if the browser couldn't guess the type.
            setErr(t('upload.unsupported'));
            return;
        }
        setFile(f);
    }

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) pickFile(f);
    }, [t]);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        if (!file || !activeCntId) return;
        setBusy(true); setErr(null);
        try {
            const form = new FormData();
            form.append('file', file);
            form.append('cntId', String(activeCntId));
            if (notes.trim()) form.append('notes', notes.trim());
            await apiFetch<UploadResponse>('/api/uploads', { method: 'POST', form });
            setDone(true);
            setTimeout(() => navigate('/app'), 1600);
        } catch (e) {
            if (e instanceof ApiError) {
                if (e.code === 'TooLarge')      setErr(t('upload.tooLarge'));
                else if (e.code === 'UnsupportedMedia') setErr(t('upload.unsupported'));
                else setErr(e.message);
            } else {
                setErr(t('auth.errors.unknown'));
            }
        } finally { setBusy(false); }
    }

    // Reset state when active unit changes mid-flow.
    useEffect(() => { setFile(null); setNotes(''); setErr(null); setDone(false); }, [activeCntId]);

    return (
        <AppShell>
            <div className="max-w-[760px] mx-auto px-6 sm:px-12 py-12">
                <header className="stagger mb-10">
                    <p className="eyebrow text-ember">{t('nav.upload')}</p>
                    <h1 className="font-display text-ink-on text-4xl sm:text-5xl tracking-tight mt-2">{t('upload.title')}</h1>
                    <p className="font-body text-ink-muted mt-3 text-balance">{t('upload.sub')}</p>
                    {active && (
                        <p className="font-body text-ink-dim text-sm mt-2">
                            {active.bldNm} — {active.untNm}
                        </p>
                    )}
                </header>

                {done ? (
                    <div className="card-paper text-center py-10">
                        <p className="eyebrow text-ember mb-3">✓</p>
                        <p className="font-display text-2xl text-ink">{t('upload.success')}</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="card space-y-6">
                        <DropZone
                            file={file}
                            dragOver={dragOver}
                            onPick={() => inputRef.current?.click()}
                            onDragEnter={() => setDragOver(true)}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            onClear={() => setFile(null)}
                        />
                        <input ref={inputRef}
                               type="file"
                               className="hidden"
                               accept="application/pdf,image/jpeg,image/png,image/webp"
                               onChange={(e) => pickFile(e.target.files?.[0] || null)} />

                        <label className="block">
                            <span className="field-label">{t('upload.notesLabel')}</span>
                            <textarea className="field-textarea min-h-[80px]"
                                      value={notes}
                                      maxLength={1024}
                                      onChange={(e) => setNotes(e.target.value)} />
                        </label>

                        {err && <div role="alert" className="text-sm text-ember pl-3 border-l-2 border-ember py-1">{err}</div>}

                        <div className="flex items-center justify-between">
                            <p className="text-xs text-ink-dim">{t('upload.limits')}</p>
                            <button type="submit" className="btn btn-primary" disabled={busy || !file || !activeCntId}>
                                {busy ? t('common.loading') : t('upload.cta')}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </AppShell>
    );
}

function DropZone({ file, dragOver, onPick, onDragEnter, onDragLeave, onDrop, onClear }: {
    file: File | null; dragOver: boolean;
    onPick: () => void; onDragEnter: () => void; onDragLeave: () => void;
    onDrop: (e: React.DragEvent) => void; onClear: () => void;
}) {
    const { t } = useTranslation();
    return (
        <div
            onClick={!file ? onPick : undefined}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className={`relative cursor-pointer rounded-sm border-2 border-dashed
                        transition-colors p-10 text-center
                        ${dragOver ? 'border-ember bg-slate-fog' : 'border-slate-line bg-slate-sunken hover:border-ink-muted'}`}
        >
            {file ? (
                <div className="flex items-center justify-between gap-4">
                    <div className="text-start min-w-0">
                        <p className="font-body text-ink-on text-sm truncate">{file.name}</p>
                        <p className="font-body text-ink-muted text-xs mt-1">
                            {fmtFileSize(file.size)} · {file.type || 'unknown'}
                        </p>
                    </div>
                    <button type="button" onClick={onClear} className="btn-link text-sm">
                        {t('common.cancel')}
                    </button>
                </div>
            ) : (
                <div>
                    <p className="font-display text-ink-on text-lg">{t('upload.dropHere')}</p>
                    <p className="text-ink-muted text-sm mt-2">{t('upload.limits')}</p>
                </div>
            )}
        </div>
    );
}
